import React, { useEffect, useState } from "react";
import { Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SaveStatusIndicator } from "./SaveStatusIndicator";
import { UnifiedMarkdownRenderer } from "@/components/ui/unified-markdown-renderer";
import { FloatingActionsPanel } from "./FloatingActionsPanel";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface ProcessedTextEditorProps {
  initialValue: string;
  onSave: (value: string) => void;
  onCancel: () => void;
  isSaving?: boolean;
  error?: string | null;
  className?: string;
}

const MAX_LENGTH = 50000; // 50KB limit

// Функция для нормализации текста (убирает лишние пробелы и переводы строк)
const normalizeText = (text: string): string => {
  return text.trim().replace(/\s+/g, ' ');
};

export const ProcessedTextEditor: React.FC<ProcessedTextEditorProps> = ({
  initialValue,
  onSave,
  onCancel,
  isSaving = false,
  error = null,
  className = "",
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const [hasChanges, setHasChanges] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [initialNormalizedValue, setInitialNormalizedValue] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (initialNormalizedValue === null) return;

    // Сравниваем нормализованные версии текста
    const currentNormalized = normalizeText(value);
    const originalNormalized = normalizeText(initialValue);

    setHasChanges(userInteracted && currentNormalized !== originalNormalized);
  }, [value, initialNormalizedValue, userInteracted, initialValue]);

  const handleSave = () => {
    if (hasChanges && !isSaving) {
      onSave(value);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmed = window.confirm(
        t('records.processed_text.cancel_confirm')
      );
      if (!confirmed) return;
    }
    onCancel();
  };

  const handleValueChange = (newValue: string) => {
    setValue(newValue);

    // Сохраняем первое нормализованное значение от MDXEditor
    if (initialNormalizedValue === null) {
      setInitialNormalizedValue(newValue);
      return;
    }

    // Теперь отмечаем реальное взаимодействие пользователя
    if (!userInteracted) {
      setUserInteracted(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Отмечаем взаимодействие пользователя при любом нажатии клавиши
    if (!userInteracted) {
      setUserInteracted(true);
    }

    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const characterCount = value.length;
  const isOverLimit = characterCount > MAX_LENGTH;

  const getStatusIndicatorStatus = () => {
    if (error) return "error";
    if (isSaving) return "saving";
    if (hasChanges) return "unsaved";
    return "saved";
  };

  const getFloatingPanelStatus = () => {
    if (error) return "error";
    if (isSaving) return "saving";
    if (hasChanges) return "unsaved";
    return "saved";
  };



  return (
    <div className={cn("space-y-4 min-h-fit w-full", isMobile && "pt-20", className)}>

      {/* Unified Markdown Editor */}
      <div className="relative w-full min-h-fit flex-1">
        <UnifiedMarkdownRenderer
          value={value}
          onChange={handleValueChange}
          onKeyDown={handleKeyDown}
          placeholder={t('records.processed_text.placeholder')}
          mode="edit"
          height={isMobile ? 400 : 600}
          readOnly={isSaving}
          className={cn(
            isOverLimit && "[&_.mdxeditor]:!border-destructive"
          )}
        />

        {/* Character counter */}
        <div
          id="char-counter"
          className={cn(
            "absolute bottom-2 right-2 text-xs px-2 py-1 rounded",
            "bg-background/80 backdrop-blur-sm border",
            isOverLimit
              ? "text-destructive border-destructive"
              : "text-muted-foreground border-border"
          )}
        >
          {characterCount.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
        </div>
      </div>


      {/* Error message */}
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded border border-destructive/20">
          {error}
        </div>
      )}

      {/* Over limit warning */}
      {isOverLimit && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded border border-destructive/20">
          {t('records.processed_text.over_limit', { chars: (characterCount - MAX_LENGTH).toLocaleString() })}
        </div>
      )}

      {/* Floating actions panel for all devices */}
      <FloatingActionsPanel
        isEditing={true}
        hasChanges={hasChanges}
        isSaving={isSaving}
        onSave={handleSave}
        onCancel={handleCancel}
        error={error}
        status={getFloatingPanelStatus()}
        position="top-right-below-header"
      />
    </div>
  );
};